import { getDatabase, ref, get } from 'firebase/database';
import { BuildingCheckTemplate } from './types';
import { formatDateOnlyForStorage, getDaysUntilDue as getDaysUntilDueFromUtils } from '../../Helpers/DateUtils';
import { PhotoModel, BlueprintLocation, BaseComponent } from '../../types/protocols';
import { isLiveComponent } from '../../Helpers/assessmentGroups';

// Interval type shared between templates and checks
export type IntervalUnit = 'days' | 'weeks' | 'months' | 'years';

/**
 * A check at `inspections/active/{buildingKey}/{checkKey}`. The key is a push key, except
 * Skickbedömning's fixed `conditionAssessment_{group}`; the template is always the
 * `templateId` field (fixed once written), never the key. A building may have several
 * checks of one template, each its own schedule.
 */
export interface BuildingCheck {
    templateId: string;
    startDate: string;
    nextDueDate: string;
    addedBy: string;
    addedTime: string;
    lastCompletedAt?: string;        // Last completion, YYYY-MM-DD hh:mm:ss (completeInspection callable); read it through lastCompletedDate()
    lastCompletedBy?: string;        // UID of who completed it last
    lastMeasurement?: {                // Last measurement value (if applicable)
        value: number;
        unit: string;
        withinRange: boolean;
    };

    /** Tells checks of one template apart ("Hus A"); see checkDisplayName. */
    label?: string;

    // Building-specific customizations (template values read at runtime)
    buildingNotes?: string;                              // Appends to template.description
    customInterval?: { value: number; unit: IntervalUnit };  // Overrides template.interval
    customCost?: number;                                 // Overrides template.estimatedCost
    additionalAttachments?: { [key: string]: PhotoModel }; // Merges with template attachments

    /**
     * The one component the check is about; absent = building-wide. Fixed once written
     * (rule): to move a check, remove it and create a new one. A component is in at most
     * one check per template. Skickbedömning has none: its template's group decides.
     */
    componentKey?: string;

    // Unit linking (lägenhet/lokal; units plan phase 3).
    unitKey?: string;
    unitLabel?: string;

    // Location marking (uses existing BlueprintLocation interface)
    location?: {
        type: 'blueprint' | 'satellite';
        sourceKey: string;              // Blueprint key or 'satellite'
        marker: BlueprintLocation;      // Includes x, y, width, height
    };

    // Snooze fields (admin-only, temporary hide from active list)
    snoozedUntil?: string;  // YYYY-MM-DD format
    snoozedBy?: string;     // UID of admin who snoozed
    snoozedAt?: string;     // YYYY-MM-DD hh:mm:ss format
}

export interface BuildingCheckWithTemplate extends BuildingCheck {
    /** Key under inspections/active/{buildingKey}; not stored on the node. */
    checkKey: string;
    template: BuildingCheckTemplate;
    buildingId?: string;
    buildingName?: string;
}

/** A completion at `inspections/completions/{buildingKey}/{checkKey}/{YYYY-MM-DD}`, written only by completeInspection. */
export interface CheckCompletion {
    templateId: string;
    checkKey?: string;
    completedBy: string;
    completedAt: string;        // When the work was actually done (can be backdated)
    submittedAt: string;        // When the data was submitted to the system
    actualDueDate: string;      // The date it was actually due
    nextDueDate: string;        // Calculated next due date
    measurement?: {
        value: number;
        unit: string;
        withinRange: boolean;
    };
    summaryComment?: string;    // Free text comment
    checkLabel?: string;        // Display name of the check when it was completed
    photos?: { [key: string]: PhotoModel };  // Uploaded photos keyed by photoKey
    componentCount?: number;    // Number of components reviewed (0 for web quick-completion)
    source?: 'web' | 'ios';     // Which client created the completion
}

/**
 * A check node read from the database, or null when it is malformed (no templateId or
 * nextDueDate). The key is the check key; the template comes from the field.
 */
export const parseBuildingCheck = (checkKey: string, data: unknown): BuildingCheck | null => {
    if (!data || typeof data !== 'object') return null;
    const d = data as Record<string, unknown>;
    if (typeof d.templateId !== 'string' || !d.templateId || typeof d.nextDueDate !== 'string') {
        console.warn(`Skipping malformed check ${checkKey}`);
        return null;
    }
    return d as unknown as BuildingCheck;
};

export interface CheckNameOptions {
    /** Name of a live component, undefined when it is gone or archived */
    componentName?: (key: string) => string | undefined;
}

/**
 * Name of a check in lists: "{template} – {label}" when the check has a label, else
 * "{template} – {component}" for a check whose component is live (and named by
 * `componentName`), else the template name (building-wide, component gone, or no
 * names at hand).
 */
export const checkDisplayName = (
    check: Pick<BuildingCheck, 'label' | 'componentKey'>,
    templateName: string,
    options: CheckNameOptions = {}
): string => {
    const label = check.label?.trim();
    if (label) return `${templateName} – ${label}`;
    const name = check.componentKey ? options.componentName?.(check.componentKey) : undefined;
    return name ? `${templateName} – ${name}` : templateName;
};

/**
 * Components that checks of the template already cover (a component is in at most
 * one check per template), leaving out `excludeCheckKey`.
 */
export const componentsCoveredByTemplate = (
    templateId: string,
    checks: Record<string, Pick<BuildingCheck, 'templateId' | 'componentKey'>>,
    excludeCheckKey?: string
): Set<string> => {
    const covered = new Set<string>();
    for (const [checkKey, check] of Object.entries(checks)) {
        if (checkKey === excludeCheckKey || check.templateId !== templateId || !check.componentKey) continue;
        covered.add(check.componentKey);
    }
    return covered;
};

/**
 * Live components of the type that no check of the template is about, sorted by key:
 * the "N {typ} saknar kontroll" hint (a component added later gets no check by itself).
 */
export const componentsMissingCheck = (
    templateId: string,
    specificType: string,
    components: Record<string, BaseComponent>,
    checks: Record<string, Pick<BuildingCheck, 'templateId' | 'componentKey'>>
): string[] => {
    const covered = componentsCoveredByTemplate(templateId, checks);
    return Object.entries(components)
        .filter(([key, component]) => isLiveComponent(component) && component.specificType?.value === specificType && !covered.has(key))
        .map(([key]) => key)
        .sort();
};

/** Date part (YYYY-MM-DD) of a check's last completion, whichever client wrote it; undefined when never completed. */
export const lastCompletedDate = (check: Pick<BuildingCheck, 'lastCompletedAt'>): string | undefined =>
    check.lastCompletedAt ? check.lastCompletedAt.split(' ')[0] : undefined;

export const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sv-SE');
};

export const formatInterval = (interval: { value: number; unit: string }) => {
    const unitTranslations: { [key: string]: string } = {
        'days': 'dagar',
        'weeks': 'veckor', 
        'months': 'månader',
        'years': 'år'
    };
    
    const swedishUnit = unitTranslations[interval.unit] || interval.unit;
    return `${interval.value} ${swedishUnit}`;
};

// Re-export from DateUtils for consistency
export const getDaysUntilDue = getDaysUntilDueFromUtils;

export const formatDaysUntilDue = (nextDueDate: string) => {
    const days = getDaysUntilDue(nextDueDate);
    if (days < 0) {
        return `${Math.abs(days)} dagar försenad`;
    } else if (days === 0) {
        return 'Förfaller idag';
    } else {
        return `${days} dagar kvar`;
    }
};

/**
 * Resolves effective values by merging BuildingCheck customizations with template defaults.
 * Use this to get the actual values to display or use in calculations.
 */
export const getEffectiveValues = (check: BuildingCheck, template: BuildingCheckTemplate) => ({
    description: template.description +
        (check.buildingNotes ? '\n\n' + check.buildingNotes : ''),
    interval: check.customInterval ?? template.interval,
    cost: check.customCost ?? template.estimatedCost,
    attachments: {
        ...template.instructionalAttachments,
        ...check.additionalAttachments
    }
});

/**
 * Calculates the next due date based on completion date and interval.
 *
 * @param nextDueDateCalculation - 'actual' (add interval to completion) or 'scheduled' (maintain cadence)
 * @param effectiveInterval - The interval to use (either check.customInterval or template.interval)
 * @param completedAt - When the check was completed (YYYY-MM-DD)
 * @param currentDueDate - The current due date (YYYY-MM-DD)
 * @returns The calculated next due date (YYYY-MM-DD)
 */
export const calculateNextDueDate = (
    nextDueDateCalculation: 'scheduled' | 'actual',
    effectiveInterval: { value: number; unit: IntervalUnit },
    completedAt: string,
    currentDueDate: string
): string => {
    const completedAtDate = new Date(completedAt);

    // For "actual" calculation: simply add interval to completion date
    if (nextDueDateCalculation === 'actual') {
        const nextDate = new Date(completedAt);
        switch (effectiveInterval.unit) {
            case 'days':
                nextDate.setDate(nextDate.getDate() + effectiveInterval.value);
                break;
            case 'weeks':
                nextDate.setDate(nextDate.getDate() + (effectiveInterval.value * 7));
                break;
            case 'months':
                nextDate.setMonth(nextDate.getMonth() + effectiveInterval.value);
                break;
            case 'years':
                nextDate.setFullYear(nextDate.getFullYear() + effectiveInterval.value);
                break;
        }
        return formatDateOnlyForStorage(nextDate);
    }

    // For "scheduled" calculation: maintain the regular cadence
    // Start from the current due date and keep adding intervals until we find
    // the next occurrence that's after the completion date
    let nextDate = new Date(currentDueDate);

    // Keep adding intervals until we get a date that's after the completion date
    // This maintains the schedule cadence (e.g., always on the 1st of the month)
    // Example:
    // - Due: 2024-01-01, Completed: 2024-03-15 (very late!)
    // - Interval: 1 month
    // - Next candidates: 2024-02-01 (before completion), 2024-03-01 (before completion), 2024-04-01 ✓
    while (nextDate <= completedAtDate) {
        switch (effectiveInterval.unit) {
            case 'days':
                nextDate.setDate(nextDate.getDate() + effectiveInterval.value);
                break;
            case 'weeks':
                nextDate.setDate(nextDate.getDate() + (effectiveInterval.value * 7));
                break;
            case 'months':
                nextDate.setMonth(nextDate.getMonth() + effectiveInterval.value);
                break;
            case 'years':
                nextDate.setFullYear(nextDate.getFullYear() + effectiveInterval.value);
                break;
        }
    }

    const calculatedNextDate = formatDateOnlyForStorage(nextDate);

    // Safety check: never move backwards from current due date (for backdated entries)
    // Example: Current due 2024-03-01, user backdates completion to 2024-01-15
    // We should keep 2024-03-01, not calculate backwards to 2024-02-01
    if (calculatedNextDate < currentDueDate) {
        return currentDueDate;
    }

    return calculatedNextDate;
};

export const loadBuildingChecks = async (buildingId: string, buildingName: string) => {
    const db = getDatabase();
    try {
        // Fetch both checks and all templates in parallel (O(1) instead of O(n) queries)
        const [checksSnapshot, templatesSnapshot] = await Promise.all([
            get(ref(db, `inspections/active/${buildingId}`)),
            get(ref(db, 'inspections/templates'))
        ]);

        if (checksSnapshot.exists()) {
            const checksData = checksSnapshot.val();
            const templatesData = templatesSnapshot.exists() ? templatesSnapshot.val() : {};

            // Join checks with templates locally, on each check's templateId field
            const checks: BuildingCheckWithTemplate[] = [];
            for (const [checkKey, checkData] of Object.entries(checksData)) {
                const check = parseBuildingCheck(checkKey, checkData);
                const template = check && templatesData[check.templateId];
                if (!check || !template) continue;
                checks.push({
                    ...check,
                    checkKey,
                    template: { ...template, id: check.templateId },
                    buildingId,
                    buildingName
                });
            }
            return checks;
        }
        return [];
    } catch (error) {
        console.error('Error loading building checks:', error);
        return [];
    }
};

export const loadAllBuildingChecks = async (buildings: {key: string, name: string}[]) => {
    const db = getDatabase();
    const allChecks: BuildingCheckWithTemplate[] = [];
    
    try {
        // Load entire inspections/active and inspections/templates nodes at once
        const [activeChecksSnapshot, templatesSnapshot] = await Promise.all([
            get(ref(db, 'inspections/active')),
            get(ref(db, 'inspections/templates'))
        ]);
        
        // Create building lookup map for quick access
        const buildingsMap = new Map(buildings.map(b => [b.key, b]));
        
        // Process active checks data
        if (activeChecksSnapshot.exists()) {
            const activeChecksData = activeChecksSnapshot.val();
            
            // Process templates data
            const templatesMap: {[templateId: string]: BuildingCheckTemplate} = {};
            if (templatesSnapshot.exists()) {
                const templatesData = templatesSnapshot.val();
                Object.entries(templatesData).forEach(([templateId, templateData]: [string, any]) => {
                    templatesMap[templateId] = { id: templateId, ...templateData };
                });
            }
            
            // Build final checks array
            Object.entries(activeChecksData).forEach(([buildingId, buildingChecks]: [string, any]) => {
                const building = buildingsMap.get(buildingId);
                if (building && buildingChecks) {
                    Object.entries(buildingChecks).forEach(([checkKey, checkData]: [string, unknown]) => {
                        const check = parseBuildingCheck(checkKey, checkData);
                        const template = check && templatesMap[check.templateId];
                        if (check && template) {
                            allChecks.push({
                                ...check,
                                checkKey,
                                template,
                                buildingId: building.key,
                                buildingName: building.name
                            });
                        }
                    });
                }
            });
        }
        
        // Sort by next due date (chronological order)
        return allChecks.sort((a, b) => {
            return new Date(a.nextDueDate).getTime() - new Date(b.nextDueDate).getTime();
        });
        
    } catch (error) {
        console.error('Error loading all building checks:', error);
        return [];
    }
};
